/* PublicMapLeaflet — Mapa Público interativo via React-Leaflet + OpenStreetMap.
 *
 * Tecnologia: React-Leaflet 4 + Leaflet 1.9 (sem API key).
 * Tiles: CartoDB Dark Matter (escuro) + CartoDB Positron (claro) — gratuitos.
 *
 * Funcionalidades:
 *  - Marcadores proporcionais (CircleMarker) ao volume de documentos
 *  - Pulse animation nos top 3 países
 *  - Tooltip ao hover + Popup detalhado ao clicar
 *  - Toggle claro/escuro
 *  - Tela cheia (Fullscreen API nativa)
 *  - Painel lateral com Top 5 países
 *  - Zoom/pan suave (nativo Leaflet)
 *  - Legenda fixa
 *  - Reusado tanto na home (compacto) quanto na rota /mapa-publico (full)
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Tooltip, Popup, ZoomControl } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Maximize2, Minimize2, Moon, Sun, Crosshair, Layers, X } from "lucide-react";
import DataSourceBadge from "@/components/DataSourceBadge";

// ---------------------------------------------------------------------------
// Patch global do Leaflet para tolerar re-init em React StrictMode (dev).
// Sem este patch, mensagem "Map container is already initialized" trava o map.
// (Aplicado apenas uma vez via guarda __nostradamus_patched.)
// ---------------------------------------------------------------------------
if (!L.Map.__nostradamus_patched) {
  const _originalInitContainer = L.Map.prototype._initContainer;
  L.Map.prototype._initContainer = function (id) {
    const container = id instanceof HTMLElement ? id : document.getElementById(id);
    if (container && container._leaflet_id) {
      // Limpa marcação de inicialização antes de re-inicializar.
      delete container._leaflet_id;
    }
    return _originalInitContainer.call(this, id);
  };
  L.Map.__nostradamus_patched = true;
}const fmtNum = (n) => new Intl.NumberFormat("pt-BR").format(n || 0);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");

// Tiles gratuitos (sem API key) — CartoDB / OpenStreetMap
const TILE_PROVIDERS = {
  dark: {
    url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
    attribution:
      '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors © <a href="https://carto.com/attributions">CARTO</a>',
    subdomains: "abcd",
    maxZoom: 19,
  },
  light: {
    url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
    attribution:
      '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors © <a href="https://carto.com/attributions">CARTO</a>',
    subdomains: "abcd",
    maxZoom: 19,
  },
};

// Paleta para volume (mantém visual dourado/azul institucional)
const colorScaleDark = (ratio) => {
  if (ratio >= 0.75) return "#fbbf24"; // dourado
  if (ratio >= 0.5) return "#f59e0b";  // amber
  if (ratio >= 0.25) return "#60a5fa"; // azul claro
  if (ratio >= 0.05) return "#3b82f6"; // azul médio
  return "#1e40af";                     // azul escuro
};
const colorScaleLight = (ratio) => {
  if (ratio >= 0.75) return "#d97706"; // amber forte
  if (ratio >= 0.5) return "#f59e0b";
  if (ratio >= 0.25) return "#0a2342"; // navy LSC
  if (ratio >= 0.05) return "#1f4368";
  return "#5b7fa3";
};

/**
 * @param {{ points: any[], meta?: any, height?: number, theme?: "dark"|"light",
 *           variant?: "home"|"full", onCountryClick?: (p:any)=>void,
 *           showSidePanel?: boolean, showThemeToggle?: boolean,
 *           showFullscreen?: boolean }} props
 */
export default function PublicMapLeaflet({
  points = [],
  meta = null,
  height = 560,
  theme: initialTheme = "dark",
  variant = "full",
  onCountryClick = null,
  showSidePanel = true,
  showThemeToggle = true,
  showFullscreen = true,
}) {
  const [theme, setTheme] = useState(initialTheme);
  const [fullscreen, setFullscreen] = useState(false);
  const containerRef = useRef(null);

  // Workaround para "Map container is already initialized" em React StrictMode (dev):
  // adia montagem do MapContainer um microtask, depois do duplo mount do strict mode.
  const [mapReady, setMapReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => { if (!cancelled) setMapReady(true); }, 0);
    return () => { cancelled = true; clearTimeout(t); setMapReady(false); };
  }, []);

  // ---- Computed
  const sorted = useMemo(
    () => [...points].sort((a, b) => (b.total_documents || 0) - (a.total_documents || 0)),
    [points],
  );
  const maxVol = useMemo(
    () => Math.max(1, ...points.map((p) => p.total_documents || 0)),
    [points],
  );
  const top5 = sorted.slice(0, 5);
  const totalDocs = points.reduce((acc, p) => acc + (p.total_documents || 0), 0);
  const topThreeCodes = sorted.slice(0, 3).map((p) => p.country_code);

  const scale = theme === "dark" ? colorScaleDark : colorScaleLight;

  // ---- Fullscreen handlers (Fullscreen API nativa)
  const toggleFullscreen = async () => {
    const el = containerRef.current;
    if (!el) return;
    try {
      if (!document.fullscreenElement) {
        await el.requestFullscreen();
        setFullscreen(true);
      } else {
        await document.exitFullscreen();
        setFullscreen(false);
      }
    } catch (err) { console.warn("Fullscreen toggle falhou:", err); }
  };
  useEffect(() => {
    const onFsChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  // ---- Pulse CSS injection — atualiza dinamicamente ao trocar tema
  useEffect(() => {
    const id = "leaflet-pulse-css";
    let style = document.getElementById(id);
    if (!style) {
      style = document.createElement("style");
      style.id = id;
      document.head.appendChild(style);
    }
    style.textContent = `
      @keyframes leaflet-pulse-ring {
        0%   { transform: scale(0.8); opacity: 0.9; }
        70%  { transform: scale(2.6); opacity: 0; }
        100% { transform: scale(2.6); opacity: 0; }
      }
      .lp-marker { position: relative; }
      .lp-marker::after {
        content: "";
        position: absolute; inset: 0;
        border-radius: 9999px;
        background: rgba(251,191,36,0.55);
        animation: leaflet-pulse-ring 2.2s ease-out infinite;
        pointer-events: none;
      }
      /* Override popup styles */
      .leaflet-popup-content-wrapper {
        background: ${theme === "dark" ? "rgba(10,35,66,0.96)" : "#ffffff"};
        color: ${theme === "dark" ? "#fff" : "#0a2342"};
        border-radius: 8px;
        border: 1px solid ${theme === "dark" ? "rgba(251,191,36,0.35)" : "#e2e8f0"};
        box-shadow: 0 12px 40px -10px rgba(0,0,0,0.5);
      }
      .leaflet-popup-tip {
        background: ${theme === "dark" ? "rgba(10,35,66,0.96)" : "#ffffff"} !important;
        border: 1px solid ${theme === "dark" ? "rgba(251,191,36,0.35)" : "#e2e8f0"};
      }
      .leaflet-popup-close-button { color: ${theme === "dark" ? "#fbbf24" : "#0a2342"} !important; }
      .leaflet-tooltip {
        background: ${theme === "dark" ? "rgba(10,35,66,0.95)" : "#ffffff"} !important;
        color: ${theme === "dark" ? "#fff" : "#0a2342"} !important;
        border: 1px solid ${theme === "dark" ? "rgba(251,191,36,0.35)" : "#e2e8f0"} !important;
        font-family: 'IBM Plex Sans', sans-serif;
        font-size: 11px;
        padding: 4px 8px;
      }
      .leaflet-tooltip-top:before { border-top-color: ${theme === "dark" ? "rgba(251,191,36,0.35)" : "#e2e8f0"} !important; }
    `;
    return () => { document.getElementById(id)?.remove(); };
  }, [theme]);

  const tile = TILE_PROVIDERS[theme];
  const themedBg = theme === "dark" ? "bg-[#0a2342]" : "bg-white";
  const themedText = theme === "dark" ? "text-amber-200" : "text-[#0a2342]";

  return (
    <div
      ref={containerRef}
      className={`relative rounded-xl overflow-hidden shadow-[0_20px_60px_-25px_rgba(10,35,66,0.55)] ring-1 ${
        theme === "dark" ? "ring-amber-400/25" : "ring-slate-200"
      } ${fullscreen ? "fixed inset-0 z-[9999] rounded-none" : ""}`}
      data-testid="public-map-leaflet"
    >
      {/* Borda decorativa superior dourada */}
      <div className="h-[3px] w-full bg-gradient-to-r from-transparent via-amber-400 to-transparent absolute top-0 left-0 z-[400]" />

      <div className={`flex ${fullscreen ? "h-screen" : ""}`}>
        {/* Container do mapa */}
        <div
          className={`${themedBg} relative flex-1 min-w-0`}
          style={{ height: fullscreen ? "100vh" : height }}
        >
          {mapReady && (
        <MapContainer
          center={[15, -10]}
          zoom={variant === "home" ? 2 : 2}
          minZoom={2}
          maxZoom={8}
          worldCopyJump
          zoomControl={false}
          scrollWheelZoom
          style={{ height: "100%", width: "100%", background: theme === "dark" ? "#0a2342" : "#f1f5f9" }}
        >
          <TileLayer
            key={theme}  // força re-render ao trocar tema
            url={tile.url}
            attribution={tile.attribution}
            subdomains={tile.subdomains}
            maxZoom={tile.maxZoom}
          />
          <ZoomControl position="bottomright" />

          {points.map((p) => {
            const count = p.total_documents || 0;
            const ratio = count / maxVol;
            // raio proporcional (escala suave 6 → 28px)
            const radius = Math.max(6, Math.min(28, 6 + ratio * 22));
            const fill = scale(ratio);
            const isTop = topThreeCodes.includes(p.country_code);
            return (
              <CircleMarker
                key={p.country_code || p.country}
                center={[p.latitude, p.longitude]}
                radius={radius}
                pathOptions={{
                  color: isTop ? "#fbbf24" : fill,
                  weight: isTop ? 2 : 1.2,
                  fillColor: fill,
                  fillOpacity: 0.78,
                  className: isTop ? "lp-marker-svg" : "",
                }}
                eventHandlers={{
                  click: () => onCountryClick && onCountryClick(p),
                }}
              >
                <Tooltip direction="top" offset={[0, -8]} opacity={1}>
                  <div className="text-[11px]">
                    <strong>{p.country}</strong> · {fmtNum(count)} pub.
                  </div>
                </Tooltip>
                <Popup>
                  <div className="text-xs" style={{ minWidth: 200 }}>
                    <div className="font-display font-bold text-[15px] tracking-tight">{p.country}</div>
                    <div className="font-mono text-[10px] opacity-60 tracking-widest">{p.country_code}</div>
                    <div className="mt-2 flex items-baseline gap-1.5">
                      <span className="font-display font-black text-xl text-amber-400 leading-none">
                        {fmtNum(count)}
                      </span>
                      <span className="text-[10.5px] opacity-70">publicações</span>
                    </div>
                    <div className="mt-2 space-y-0.5 text-[10.5px] opacity-90">
                      <div>Últimos 7d: <strong>{fmtNum(p.documents_last_7_days)}</strong></div>
                      <div>Δ semana: <strong>{p.weekly_variation > 0 ? "+" : ""}{p.weekly_variation || 0}</strong></div>
                      <div>Vetor dominante: <strong>{p.dominant_area_label || p.dominant_area || "—"}</strong></div>
                    </div>
                    {p.area_distribution && Object.keys(p.area_distribution).length > 0 && (
                      <div className="mt-2 pt-2 border-t border-amber-400/20">
                        <div className="text-[9px] uppercase tracking-widest opacity-60 mb-1">Principais áreas</div>
                        {Object.entries(p.area_distribution)
                          .sort((a, b) => b[1] - a[1])
                          .slice(0, 4)
                          .map(([code, v]) => (
                            <div key={code} className="flex justify-between text-[10px]">
                              <span className="opacity-80">{code}</span>
                              <span className="font-mono">{v}</span>
                            </div>
                          ))}
                      </div>
                    )}
                    <div className="mt-2 text-[9px] opacity-50 font-mono">
                      Atualizado: {fmtDate(p.last_updated_at)}
                    </div>
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
        )}

        {/* Overlays — Coordenadas decorativas */}
        <div className={`absolute top-3 right-3 z-[500] text-[10px] font-mono uppercase tracking-widest ${themedText} text-right leading-tight pointer-events-none`}>
          <div className="flex items-center justify-end gap-1.5">
            <Crosshair size={11} /> LAT 0.0000°
          </div>
          <div>LON 0.0000°</div>
          <div className="mt-1 inline-flex items-center gap-1.5 text-emerald-400/90">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
            </span>
            SYS · ONLINE
          </div>
        </div>

        {/* Botões de controle */}
        <div className="absolute top-3 left-3 z-[500] flex flex-col gap-2">
          {showThemeToggle && (
            <button
              onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
              className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-[#0a2342]/85 backdrop-blur border border-amber-400/30 text-amber-300 hover:bg-[#0a2342] transition shadow-lg"
              title={theme === "dark" ? "Modo claro" : "Modo escuro"}
              data-testid="map-theme-toggle"
            >
              {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
            </button>
          )}
          {showFullscreen && (
            <button
              onClick={toggleFullscreen}
              className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-[#0a2342]/85 backdrop-blur border border-amber-400/30 text-amber-300 hover:bg-[#0a2342] transition shadow-lg"
              title={fullscreen ? "Sair da tela cheia" : "Tela cheia"}
              data-testid="map-fullscreen"
            >
              {fullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            </button>
          )}
        </div>

        {/* Legenda fixa */}
        <div className={`absolute bottom-3 left-3 z-[500] backdrop-blur-md px-3.5 py-2.5 rounded-md border shadow-xl ${
          theme === "dark"
            ? "bg-[#0a2342]/85 border-amber-400/30 text-amber-200"
            : "bg-white/95 border-slate-200 text-[#0a2342]"
        }`}>
          <div className="flex items-center gap-2 text-[9.5px] font-mono uppercase tracking-widest">
            <Layers size={11} className="opacity-70" />
            <span className="opacity-80">Volume</span>
            <Sw c={scale(0.04)} />
            <Sw c={scale(0.2)} />
            <Sw c={scale(0.4)} />
            <Sw c={scale(0.6)} />
            <Sw c={scale(0.8)} pulse />
            <span className="opacity-60">→ máx</span>
          </div>
        </div>

        {/* Selo de origem dos dados (sobre o mapa) */}
        {meta && (
          <div className="absolute bottom-3 right-16 z-[500]" data-testid="map-source-badge">
            <DataSourceBadge
              mode={meta.data_source_mode}
              shortLabel={meta.data_source_short_label}
              note={meta.methodology_note}
            />
          </div>
        )}

        {/* Marca d'água institucional */}
        <div className={`absolute bottom-12 left-3 z-[400] text-[9px] font-mono uppercase tracking-[0.25em] pointer-events-none ${
          theme === "dark" ? "text-amber-300/40" : "text-slate-400/70"
        }`}>
          LSC · EGN · Projeto Nostradamus
        </div>

        {fullscreen && (
          <button
            onClick={toggleFullscreen}
            className="absolute top-3 right-32 z-[600] inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 bg-[#0a2342]/90 text-amber-300 rounded border border-amber-400/40 hover:bg-[#0a2342]"
            data-testid="map-exit-fullscreen"
          >
            <X size={12} /> Sair
          </button>
        )}
        </div>

        {/* Painel lateral — Top 5 países (side-by-side layout) */}
        {showSidePanel && !fullscreen && (
          <aside
            className="w-[260px] shrink-0 bg-[#0a2342] text-white px-5 py-6 hidden lg:flex flex-col border-l border-amber-400/30 shadow-[-10px_0_25px_-15px_rgba(0,0,0,0.5)]"
            data-testid="map-side-panel-leaflet"
            style={{ height: height }}
          >
          <div className="flex items-center gap-2 mb-1">
            <Crosshair size={13} className="text-amber-400" />
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-amber-400">Focos prioritários</div>
          </div>
          <h4 className="font-display font-bold text-lg leading-tight mb-4">Top 5 países monitorados</h4>

          <div className="space-y-2.5 flex-1 overflow-y-auto pr-1">
            {top5.length === 0 && (
              <div className="text-[11px] text-slate-400 font-mono">Carregando focos…</div>
            )}
            {top5.map((p, idx) => {
              const pct = maxVol > 0 ? Math.round(((p.total_documents || 0) / maxVol) * 100) : 0;
              return (
                <button
                  key={p.country_code || p.country}
                  onClick={() => onCountryClick && onCountryClick(p)}
                  className="group w-full text-left hover:bg-white/5 -mx-2 px-2 py-1.5 rounded transition"
                  data-testid={`map-top-${idx}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-[10px] text-amber-400/80 w-4">#{idx + 1}</span>
                      <span className="text-[13px] font-medium truncate">{p.country}</span>
                    </div>
                    <span className="font-display font-bold text-amber-400 text-sm tabular-nums">
                      {fmtNum(p.total_documents)}
                    </span>
                  </div>
                  <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-amber-500 to-amber-300 rounded-full transition-all duration-700"
                      style={{ width: `${Math.max(pct, 4)}%` }}
                    />
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-5 pt-4 border-t border-white/10">
            <div className="text-[9px] font-mono uppercase tracking-[0.22em] text-slate-400 mb-1">Cobertura total</div>
            <div className="flex items-baseline gap-2">
              <span className="font-display font-black text-2xl text-white tabular-nums">{fmtNum(points.length)}</span>
              <span className="text-[11px] text-slate-400">países · {fmtNum(totalDocs)} docs</span>
            </div>
          </div>
        </aside>
      )}
      </div>
    </div>
  );
}

function Sw({ c, pulse }) {
  return (
    <span
      className={`inline-block w-3 h-3 rounded-sm ${pulse ? "animate-pulse" : ""}`}
      style={{ background: c, boxShadow: pulse ? `0 0 8px ${c}` : "none" }}
    />
  );
}
